import { useEffect, useRef } from "react";
import { recordPollingTick } from "../utils/serverClock";

const INTERACTION_MIN_MS = 5000;

// Ejecuta `callback` cada `intervalMs` y, además, AL INSTANTE cada vez que la
// página vuelve a primer plano (desbloquear el celular, volver a la pestaña o
// a la app, o que el navegador la restaure de su caché).
//
// Por qué no un setInterval a secas: iOS (Safari y Chrome, ambos WebKit)
// congela los timers cuando la pantalla se bloquea o la app pasa a segundo
// plano, y al volver pueden tardar en reanudarse — o no reanudarse. La
// pantalla quedaba mostrando lo último que dibujó (el contador "hace X min"
// del Cliente se veía detenido en el iPhone). Con esto, al volver se
// actualiza enseguida y el timer se reprograma desde cero.
//
// Se usa un setTimeout que se reprograma solo (no setInterval): así, al volver
// a primer plano, se cancela el pendiente y se arranca de nuevo, sin dejar
// dos timers corriendo.
export function usePolling(callback, intervalMs, { enabled = true, immediate = false } = {}) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return undefined;
    let timeoutId = null;
    let stopped = false;
    let lastRun = 0;

    const schedule = () => {
      clearTimeout(timeoutId);
      if (!stopped) timeoutId = setTimeout(() => tick("timer"), intervalMs);
    };
    const tick = (source) => {
      if (stopped) return;
      lastRun = Date.now();
      recordPollingTick(source);
      try {
        callbackRef.current();
      } finally {
        schedule();
      }
    };
    // visibilitychange solo cuando pasa a visible; focus/pageshow siempre (por
    // si el navegador informa mal la visibilidad).
    const onResume = (event) => {
      if (event.type === "visibilitychange" && document.visibilityState !== "visible") return;
      tick("resume");
    };
    // Respaldo: en un iPhone real los timers casi no corrían aun con la
    // pantalla visible y en uso — el contador solo se actualizaba al salir y
    // volver a entrar. Cualquier toque o desplazamiento también refresca (como
    // mucho una vez cada INTERACTION_MIN_MS, para no saturar el API).
    const onInteract = () => {
      if (Date.now() - lastRun >= Math.min(INTERACTION_MIN_MS, intervalMs)) tick("touch");
    };

    if (immediate) tick("start");
    else schedule();

    const passive = { passive: true };
    document.addEventListener("visibilitychange", onResume);
    window.addEventListener("focus", onResume);
    window.addEventListener("pageshow", onResume);
    window.addEventListener("touchstart", onInteract, passive);
    window.addEventListener("scroll", onInteract, passive);
    window.addEventListener("pointerdown", onInteract, passive);
    return () => {
      stopped = true;
      clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onResume);
      window.removeEventListener("focus", onResume);
      window.removeEventListener("pageshow", onResume);
      window.removeEventListener("touchstart", onInteract, passive);
      window.removeEventListener("scroll", onInteract, passive);
      window.removeEventListener("pointerdown", onInteract, passive);
    };
  }, [intervalMs, enabled, immediate]);
}
